import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { generationsApi, snapshotApi } from "@/lib/api/endpoints";
import { captureFromVideo, fileToDataUrl } from "@/lib/utils/image";
import { resolveAssetUrl } from "@/components/ui";
import type { FaceArea, FrameConfig } from "@/lib/types/config";
import { useBooth } from "../BoothContext";

type Phase = "starting" | "live" | "countdown" | "review" | "error";

const COUNTDOWN_FROM = 3;

/** Booths are portrait. Used whenever no frame says otherwise. */
const DEFAULT_ASPECT = 4 / 3;

/**
 * Camera capture.
 *
 * The preview is built to be a *rehearsal of the final image*, not just a
 * viewfinder. That means two things:
 *
 *   1. The preview box takes the frame's own aspect ratio, and the frame is
 *      drawn edge to edge inside it. Previously the box was hardcoded 3:4 and
 *      the overlay used `object-contain`, so a frame of any other shape was
 *      letterboxed with the raw camera feed visible around it.
 *   2. The camera sits *inside the frame's cutout*, at the cutout's position and
 *      size — which is exactly where the server will paste the photo. So what
 *      the guest lines up is what they get.
 *
 * The capture is then cropped to the cutout's aspect ratio before it's sent, so
 * the server never has to throw away part of the guest's head to make it fit.
 *
 * Falls back to a file picker when there's no camera or permission is refused —
 * a kiosk with a broken webcam should still be able to take a booking.
 */
export function CaptureScreen() {
  const booth = useBooth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [phase, setPhase] = useState<Phase>("starting");
  const [count, setCount] = useState(COUNTDOWN_FROM);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const hardware = booth.config.event.hardware;
  const isSnapshot = booth.mode === "snapshot";
  const filters = booth.config.snapshot?.filters ?? [];
  const activeFilter = filters.find((item) => item.slug === booth.filterSlug) ?? filters[0];
  // Only the fields this blueprint asks for at capture time.
  const captureFields = (booth.config.experience?.fields ?? [])
    .filter((field) => field.step === "capture");
  const frame = booth.config.event.frames?.[0] ?? booth.config.event.frame;
  const overlay = frame?.enabled !== false ? resolveAssetUrl(frame?.overlay_path) : undefined;
  const faceArea = frame?.face_area;

  // Shape of the preview box: the frame's own ratio, so the overlay fills it
  // exactly and nothing shows around the edges. Portrait when there's no frame.
  const boxAspect = frameAspect(frame) ?? DEFAULT_ASPECT;
  // Where the camera goes within that box. Only honoured when there's an overlay
  // to sit inside — a cutout with no frame drawn around it is just a crop.
  const cutout = overlay ? faceAreaBounds(faceArea) : null;
  // What the capture is cropped to before sending: the hole it's going into.
  const captureAspect = cutoutAspect(frame, cutout) ?? DEFAULT_ASPECT;

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      if (hardware?.camera_type === "none") {
        setPhase("error");
        setError("This booth is set up for uploads rather than a live camera.");
        return;
      }

      try {
        const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
        const videoConstraints: MediaTrackConstraints = {
            facingMode: "user",
            // Ask for portrait. A photo booth is a vertical format, and asking
            // for 1920x1080 was why every capture came out landscape.
            //
            // These are `ideal`, not `exact`: most laptop webcams are physically
            // landscape and will hand back 1280x720 regardless. That's fine — we
            // crop to `captureAspect` after the grab, so the sent pixels are
            // portrait either way. Asking still helps on phones and on cameras
            // that can genuinely oblige, where cropping then throws away less.
            width: { ideal: 1440 },
            height: { ideal: 1920 },
            aspectRatio: { ideal: captureAspect },
            // A device id belongs to the computer where Hardware was configured
            // and is invalid on a guest's phone. Let mobile use its own camera.
            ...(!mobile && hardware?.camera_device ? { deviceId: { exact: hardware.camera_device } } : {}),
        };
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: false });
        } catch (firstError) {
          // If a saved desktop device disappeared or became unavailable, retry
          // with the browser's default camera before showing an error.
          if (!mobile && hardware?.camera_device && firstError instanceof DOMException && firstError.name === "OverconstrainedError") {
            stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
          } else {
            throw firstError;
          }
        }

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setPhase("live");
      } catch (caught) {
        if (cancelled) return;
        setPhase("error");
        setError(
          caught instanceof DOMException && caught.name === "NotAllowedError"
            ? "Camera access was blocked. Allow it in your browser, or upload a photo instead."
            : "No camera is available on this device.",
        );
      }
    }

    void start();
    return () => { cancelled = true; stopCamera(); };
  }, [hardware?.camera_device, hardware?.camera_type, captureAspect, stopCamera]);

  // Countdown, then preserve the complete camera frame. Presentation and
  // export use contain-fit so the same framing survives every stage.
  useEffect(() => {
    if (phase !== "countdown") return;

    if (count <= 0) {
      let cancelled = false;

      void (async () => {
        try {
          if (!videoRef.current) throw new Error("Camera is not ready");
          const raw = captureFromVideo(videoRef.current);
          if (cancelled) return;
          booth.setCapture(raw);
          setPhase("review");
        } catch {
          if (!cancelled) setPhase("live");
        }
      })();

      return () => { cancelled = true; };
    }

    const timer = window.setTimeout(() => setCount((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [phase, count, captureAspect, booth]);

  async function submit() {
    if (!booth.capture) return;
    setSubmitting(true);

    try {
      if (isSnapshot) {
        const result = await snapshotApi.create(booth.slug, {
          image: booth.capture,
          filter_slug: booth.filterSlug,
          guest_fields: booth.guestFields,
        });
        stopCamera();

        // Capture is free — the guest pays on the way out, from the result
        // screen, for whichever action they want.
        booth.showResult({
          snapshotId: result.id,
          shareToken: result.public_token,
          url: result.image_url ?? booth.capture,
          actions: result.actions,
        });
        return;
      }

      if (!booth.scene) return;

      const handle = await generationsApi.create(booth.slug, {
        scene_slug: booth.scene.slug,
        participant_slug: booth.participant?.slug ?? null,
        source_image: booth.capture,
      });
      stopCamera();
      // The processing screen owns polling from here.
      booth.startGeneration({ generationId: handle.id, shareToken: handle.public_token });
    } catch (caught) {
      setSubmitting(false);
      setError(caught instanceof Error ? caught.message : "Could not start your photo. Please try again.");
    }
  }

  async function onUpload(file: File | undefined) {
    if (!file) return;
    try {
      booth.setCapture(await fileToDataUrl(file));
      setPhase("review");
      setError(null);
    } catch {
      setError("That file couldn't be read. Try a different photo.");
    }
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 px-6 py-20">
      <div
        className="relative w-full max-w-md overflow-hidden rounded-3xl shadow-2xl"
        style={{
          // The frame's own ratio, so the overlay below fills this box exactly.
          aspectRatio: `${boxAspect}`,
          // White behind a frame (its transparent margins are meant to read as
          // paper), black when the camera is the whole picture.
          background: overlay ? "#ffffff" : "#000000",
        }}
      >
        {/* The camera lives in the cutout, at the cutout's position — the same
            rectangle the server pastes the finished photo into. */}
        <div
          className="absolute overflow-hidden"
          style={
            cutout
              ? {
                  left: `${cutout.x * 100}%`,
                  top: `${cutout.y * 100}%`,
                  width: `${cutout.width * 100}%`,
                  height: `${cutout.height * 100}%`,
                }
              : { inset: 0 }
          }
        >
          {phase === "review" && booth.capture ? (
            <img
              src={booth.capture}
              alt="Your photo"
              className="h-full w-full object-cover"
              // The same filter string the server re-applies for real when it
              // produces the file, so the preview is honest.
              style={{ filter: filterCss(activeFilter?.css) }}
            />
          ) : (
            <video
              ref={videoRef}
              playsInline
              muted
              className="h-full w-full object-cover"
              // Mirrored so the guest sees themselves as in a mirror; the capture
              // is mirrored to match.
              style={{ transform: "scaleX(-1)", filter: filterCss(activeFilter?.css) }}
            />
          )}
        </div>

        {/* Frame edge to edge. No object-contain: the box already matches its
            ratio, so there is nothing to letterbox. */}
        {overlay && (
          <img src={overlay} alt="" className="pointer-events-none absolute inset-0 h-full w-full" />
        )}

        {/* No frame drawn, but the config names a face area — show it as a guide
            so the guest still knows where to stand. */}
        {!overlay && faceAreaBounds(faceArea) ? (
          <div
            className="pointer-events-none absolute rounded-lg border-2 border-dashed border-white/50"
            style={{
              left: `${faceAreaBounds(faceArea)!.x * 100}%`,
              top: `${faceAreaBounds(faceArea)!.y * 100}%`,
              width: `${faceAreaBounds(faceArea)!.width * 100}%`,
              height: `${faceAreaBounds(faceArea)!.height * 100}%`,
            }}
          />
        ) : null}

        {phase === "starting" && (
          <div className="absolute inset-0 grid place-items-center bg-black/50 text-sm uppercase tracking-[0.2em] text-white/80">
            Initializing camera
          </div>
        )}

        {phase === "countdown" && (
          <div className="absolute inset-0 grid place-items-center bg-black/40">
            <span className="text-[8rem] font-bold leading-none text-white drop-shadow-lg">
              {count > 0 ? count : "📸"}
            </span>
          </div>
        )}
      </div>

      {isSnapshot && filters.length > 1 && (
        <div className="flex w-full max-w-md gap-2 overflow-x-auto pb-1">
          {filters.map((filter) => {
            const active = filter.slug === booth.filterSlug;
            return (
              <button
                key={filter.slug}
                onClick={() => booth.setFilter(filter.slug)}
                className="flex shrink-0 flex-col items-center gap-1"
              >
                <span
                  className={[
                    "grid h-14 w-14 place-items-center overflow-hidden rounded-xl border-2 transition-colors",
                    active ? "border-white" : "border-white/25",
                  ].join(" ")}
                  style={{
                    filter: filterCss(filter.css),
                    background: "linear-gradient(135deg, #f2c9a0 0%, #b5703f 55%, #2a1a12 100%)",
                  }}
                />
                <span className={active ? "text-2xs text-booth-ink" : "text-2xs text-booth-ink-muted"}>
                  {filter.name}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {isSnapshot && phase === "review" && captureFields.length > 0 && (
        <div className="flex w-full max-w-md flex-col gap-2">
          {captureFields.map((field) => (
            <label key={field.key} className="flex flex-col gap-1">
              <span className="text-2xs uppercase tracking-wider text-booth-ink-muted">{field.label}</span>
              <input
                type={field.type === "number" ? "number" : "text"}
                placeholder={field.placeholder}
                value={booth.guestFields[field.key] ?? ""}
                onChange={(e) => booth.setGuestField(field.key, e.target.value)}
                className="h-11 rounded-xl border border-booth-line bg-booth-surface-strong px-3 text-booth-ink placeholder:text-booth-ink-muted"
              />
            </label>
          ))}
        </div>
      )}

      {error && (
        <p className="max-w-md rounded-xl bg-booth-surface-strong px-4 py-3 text-center text-sm text-booth-ink">{error}</p>
      )}

      <div className="flex flex-wrap items-center justify-center gap-3">
        {phase === "live" && (
          <BoothButton onClick={() => { setCount(COUNTDOWN_FROM); setPhase("countdown"); }}>
            {booth.say("capture_verb", "Take photo")}
          </BoothButton>
        )}

        {phase === "review" && (
          <>
            <BoothButton onClick={submit} loading={submitting}>
              {booth.copy("use_photo_label", "Use this photo")}
            </BoothButton>
            <SecondaryButton
              onClick={() => { booth.setCapture(null); setPhase(streamRef.current ? "live" : "error"); }}
            >
              {booth.copy("retake_button_label", "Retake")}
            </SecondaryButton>
          </>
        )}

        {(phase === "error" || phase === "live") && (
          <label className="cursor-pointer">
            <SecondaryButton as="span">Upload instead</SecondaryButton>
            <input type="file" accept="image/*" hidden onChange={(e) => void onUpload(e.target.files?.[0])} />
          </label>
        )}
      </div>
    </div>
  );
}

/**
 * Frame geometry. These three mirror `ConfigFrames` on the server — the preview
 * and the rendered file have to agree on where the hole is, or the guest lines
 * up against one rectangle and gets pasted into another.
 */

interface Bounds { x: number; y: number; width: number; height: number }

/** Frame's own aspect ratio, from its native pixel size. */
function frameAspect(frame: FrameConfig | undefined): number | null {
  const width = frame?.native_width ?? 0;
  const height = frame?.native_height ?? 0;
  return width > 0 && height > 0 ? width / height : null;
}

/**
 * The cutout as a bounding box in fractions of the frame.
 * face_area is either a rectangle or a lasso polygon; both reduce to a box.
 */
function faceAreaBounds(area: FaceArea | undefined): Bounds | null {
  if (!area) return null;

  if (area.points && area.points.length > 0) {
    const xs = area.points.map((point) => point.x);
    const ys = area.points.map((point) => point.y);
    return {
      x: Math.min(...xs),
      y: Math.min(...ys),
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys),
    };
  }

  if (!area.width || !area.height) return null;
  return { x: area.x ?? 0, y: area.y ?? 0, width: area.width, height: area.height };
}

/**
 * Aspect ratio of the cutout *in real pixels*.
 *
 * The subtlety: face_area is stored as fractions of the frame, so a cutout that
 * is 0.5 × 0.5 is only square if the frame itself is square. The fractions have
 * to be multiplied back through the frame's native size.
 */
function cutoutAspect(frame: FrameConfig | undefined, cutout: Bounds | null): number | null {
  const aspect = frameAspect(frame);
  if (aspect === null) return null;
  if (!cutout || cutout.width <= 0 || cutout.height <= 0) return aspect;

  return (cutout.width * (frame!.native_width ?? 0)) / (cutout.height * (frame!.native_height ?? 0));
}

/** CSS `filter` won't accept the literal string "none" from config as-is. */
function filterCss(css: string | undefined): string | undefined {
  return !css || css === "none" ? undefined : css;
}

function BoothButton({
  children, onClick, loading,
}: { children: ReactNode; onClick: () => void; loading?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="rounded-xl px-10 py-4 text-lg font-bold uppercase tracking-wide transition-transform active:scale-[0.97] disabled:opacity-60"
      style={{ background: "var(--booth-secondary)", color: "var(--booth-cta-text)" }}
    >
      {loading ? "Sending…" : children}
    </button>
  );
}

function SecondaryButton({
  children, onClick, as = "button",
}: { children: ReactNode; onClick?: () => void; as?: "button" | "span" }) {
  const Tag = as;
  return (
    <Tag
      {...(as === "button" ? { onClick } : {})}
      className="inline-block rounded-xl border border-booth-line bg-booth-surface px-8 py-4 text-lg font-semibold uppercase tracking-wide text-booth-ink backdrop-blur-sm transition-transform active:scale-[0.97]"
    >
      {children}
    </Tag>
  );
}
